import React, { useState } from 'react';
import { Plus, Save, X, FileText, FileVideo, Book } from 'lucide-react';
import useSuperAdminContentManagementStore from '@/stores/SuperAdminStore/SuperAdminContentManagementStore';
import { StandaloneLessonData } from '@/services/SuperAdminLessonService';
import {
  uploadLessonDocument,
  uploadLessonMp4,
  uploadLessonScorm,
} from '@/services/UploadService';
import './UploadTab.css';

interface LessonFormData extends Partial<StandaloneLessonData> {
  title: string;
  lessonType: 'ILTS' | 'Content-Based';
  contentType?: 'document' | 'scorm' | 'mp4' | 'url';
  lessonDuration?: string;
  description?: string;
  iltsType?: 'Online' | 'Offline';
  meetUrl?: string;
  eventVenue?: string;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  contentUrl?: string;
  lessonContentDocument?: string | File;
  scormPackage?: string | File;
  videoUpload?: string | File;
}

// Icon mapping based on content type
const getIconByType = (type: 'document' | 'scorm' | 'mp4' | 'url'): React.ReactNode => {
  const iconMap = {
    mp4: <FileVideo size={20} />,
    scorm: <Book size={20} />,
    document: <FileText size={20} />,
    url: <FileText size={20} />
  };
  return iconMap[type];
};

const UploadTab: React.FC = () => {
  const [lessonFormData, setLessonFormData] = useState<LessonFormData>({
    title: '',
    lessonType: 'Content-Based',
    contentType: 'document',
    lessonDuration: '15 mins',
    description: '',
  });
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const {
    createLesson,
    fetchStandaloneLessons,
  } = useSuperAdminContentManagementStore();

  const handleResetForm = () => {
    setLessonFormData({
      title: '',
      lessonType: 'Content-Based',
      contentType: 'document',
      lessonDuration: '15 mins',
      description: '',
    });
    setUploadedFile(null);
    setError(null);
  };

  const updateLessonFormField = (field: string, value: any) => {
    setLessonFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveLesson = async () => {
    if (!lessonFormData.title.trim()) {
      setError('Lesson title is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Create lesson without file reference first
      const lessonData: StandaloneLessonData = {
        title: lessonFormData.title,
        lessonType: lessonFormData.lessonType,
        lessonDuration: lessonFormData.lessonDuration,
        description: lessonFormData.description,
      };

      // Add type-specific fields
      if (lessonFormData.lessonType === 'Content-Based') {
        lessonData.contentType = lessonFormData.contentType;
        if (lessonFormData.contentType === 'url') {
          lessonData.contentUrl = lessonFormData.contentUrl;
        }
      } else if (lessonFormData.lessonType === 'ILTS') {
        lessonData.iltsType = lessonFormData.iltsType || 'Online';
        lessonData.startDate = lessonFormData.startDate;
        lessonData.startTime = lessonFormData.startTime;
        lessonData.endDate = lessonFormData.endDate;
        lessonData.endTime = lessonFormData.endTime;

        if (lessonFormData.iltsType === 'Online') {
          lessonData.meetUrl = lessonFormData.meetUrl;
        } else {
          lessonData.eventVenue = lessonFormData.eventVenue;
        }
      }

      const result = await createLesson(lessonData);

      if (result) {
        const lessonId = result.id;

        // Upload file if one was selected and it's Content-Based
        if (uploadedFile && lessonFormData.lessonType === 'Content-Based') {
          try {
            let uploadResult;

            if (lessonFormData.contentType === 'document') {
              uploadResult = await uploadLessonDocument(uploadedFile, lessonId);
            } else if (lessonFormData.contentType === 'mp4') {
              uploadResult = await uploadLessonMp4(uploadedFile, lessonId);
            } else if (lessonFormData.contentType === 'scorm') {
              uploadResult = await uploadLessonScorm(uploadedFile, lessonId);
            }

            if (uploadResult && !uploadResult.success) {
              setError('Lesson created but file upload failed');
              setSaving(false);
              return;
            }
          } catch (uploadError: any) {
            console.error('File upload error:', uploadError);
            setError('Lesson created but file upload failed: ' + uploadError.message);
            setSaving(false);
            return;
          }
        }

        // Explicitly refresh the lessons list
        await fetchStandaloneLessons();

        setSuccess('Lesson created successfully! You can view it in the Library tab.');
        handleResetForm();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError('Failed to create lesson');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save lesson');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="s-admin-upload-container">
      <div className="s-admin-upload-main-section">
        {/* Header */}
        <div className="s-admin-upload-section-box">
          <h3 className="s-admin-upload-section-title">Create Lesson</h3>
          <p className="s-admin-upload-subtext" style={{ marginTop: '8px', marginBottom: 0 }}>
            Create lessons that can be associated with courses later
          </p>
        </div>

        {/* Status Messages */}
        {error && (
          <div style={{
            backgroundColor: '#ffebee',
            color: '#c62828',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <p style={{ margin: 0 }}>{error}</p>
            <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <X size={16} color="#c62828" />
            </button>
          </div>
        )}

        {success && (
          <div style={{
            backgroundColor: '#e8f5e9',
            color: '#2e7d32',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '16px',
          }}>
            <p style={{ margin: 0 }}>{success}</p>
          </div>
        )}

        {/* Lesson Form */}
        <div className="s-admin-upload-section-box">
          <div className="form-row">
            <div className="form-group">
              <label>Lesson Title *</label>
              <input
                type="text"
                value={lessonFormData.title}
                onChange={(e) => updateLessonFormField('title', e.target.value)}
                placeholder="Enter lesson title"
                className="form-input"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Lesson Type *</label>
              <select
                value={lessonFormData.lessonType}
                onChange={(e) => {
                  const newLessonType = e.target.value as 'ILTS' | 'Content-Based';
                  setLessonFormData(prev => ({
                    ...prev,
                    lessonType: newLessonType,
                    ...(newLessonType === 'ILTS' ? { iltsType: 'Online' } : { contentType: 'document' })
                  }));
                }}
                className="form-select"
              >
                <option value="Content-Based">Content-Based</option>
                <option value="ILTS">Instructor-Led Training Session (ILTS)</option>
              </select>
            </div>

            <div className="form-group">
              <label>Duration</label>
              <input
                type="text"
                value={lessonFormData.lessonDuration || ''}
                onChange={(e) => updateLessonFormField('lessonDuration', e.target.value)}
                placeholder="e.g., 30 mins"
                className="form-input"
              />
            </div>
          </div>

          {lessonFormData.lessonType === 'Content-Based' && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>Content Type *</label>
                  <select
                    value={lessonFormData.contentType || 'document'}
                    onChange={(e) => {
                      const newContentType = e.target.value as 'document' | 'scorm' | 'mp4' | 'url';
                      setLessonFormData(prev => ({
                        ...prev,
                        contentType: newContentType,
                        contentUrl: '',
                        lessonContentDocument: '',
                        videoUpload: '',
                        scormPackage: ''
                      }));
                      setUploadedFile(null);
                    }}
                    className="form-select"
                  >
                    <option value="document">Document (PDF/DOC)</option>
                    <option value="mp4">Video (MP4)</option>
                    <option value="scorm">SCORM Package</option>
                    <option value="url">External URL</option>
                  </select>
                </div>
              </div>

              {lessonFormData.contentType === 'url' ? (
                <div className="form-row">
                  <div className="form-group">
                    <label>Content URL</label>
                    <input
                      type="url"
                      value={lessonFormData.contentUrl || ''}
                      onChange={(e) => updateLessonFormField('contentUrl', e.target.value)}
                      placeholder="https://example.com/content"
                      className="form-input"
                    />
                  </div>
                </div>
              ) : (
                <div className="form-row">
                  <div className="form-group">
                    <label>Upload {lessonFormData.contentType === 'mp4' ? 'Video' : lessonFormData.contentType === 'document' ? 'Document' : 'SCORM Package'}</label>
                    <div className="s-admin-upload-dropzone"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const files = e.dataTransfer.files;
                      if (files && files[0]) {
                        setUploadedFile(files[0]);
                      }
                    }}
                    >
                      <input
                        type="file"
                        id="content-upload"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploadedFile(file);
                          }
                        }}
                        accept={
                          lessonFormData.contentType === 'mp4' ? 'video/*' :
                          lessonFormData.contentType === 'document' ? '.pdf,.doc,.docx' :
                          '.zip'
                        }
                        style={{ display: 'none' }}
                      />
                      <label htmlFor="content-upload" className="s-admin-upload-dropzone-label">
                        <div className="s-admin-upload-dropzone-content">
                          {uploadedFile ? (
                            <div className="s-admin-upload-dropzone-success">
                              ✓ {uploadedFile.name}
                            </div>
                          ) : (
                            <>
                              <div className="s-admin-upload-dropzone-text">📁 Click to upload or drag and drop</div>
                              <div className="s-admin-upload-dropzone-hint">
                                {lessonFormData.contentType === 'mp4' && 'MP4, MOV, AVI up to 500MB'}
                                {lessonFormData.contentType === 'document' && 'PDF, DOC, DOCX up to 50MB'}
                                {lessonFormData.contentType === 'scorm' && 'ZIP file containing SCORM package'}
                              </div>
                            </>
                          )}
                        </div>
                      </label>
                    </div>
                    <p className="s-admin-upload-dropzone-note">
                      Note: File will be uploaded after the lesson is saved
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {lessonFormData.lessonType === 'ILTS' && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>Session Type *</label>
                  <select
                    value={lessonFormData.iltsType || 'Online'}
                    onChange={(e) => {
                      const newIltsType = e.target.value as 'Online' | 'Offline';
                      setLessonFormData(prev => ({
                        ...prev,
                        iltsType: newIltsType,
                        ...(newIltsType === 'Online' ? { eventVenue: '' } : { meetUrl: '' })
                      }));
                    }}
                    className="form-select"
                  >
                    <option value="Online">Online</option>
                    <option value="Offline">Offline</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date & Time</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                      type="date"
                      value={lessonFormData.startDate || ''}
                      onChange={(e) => updateLessonFormField('startDate', e.target.value)}
                      className="form-input"
                      style={{ flex: 1 }}
                    />
                    <input
                      type="time"
                      value={lessonFormData.startTime || ''}
                      onChange={(e) => updateLessonFormField('startTime', e.target.value)}
                      className="form-input"
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>End Date & Time</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                      type="date"
                      value={lessonFormData.endDate || ''}
                      onChange={(e) => updateLessonFormField('endDate', e.target.value)}
                      className="form-input"
                      style={{ flex: 1 }}
                    />
                    <input
                      type="time"
                      value={lessonFormData.endTime || ''}
                      onChange={(e) => updateLessonFormField('endTime', e.target.value)}
                      className="form-input"
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>
              </div>

              {lessonFormData.iltsType === 'Online' && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Meeting URL</label>
                    <input
                      type="url"
                      value={lessonFormData.meetUrl || ''}
                      onChange={(e) => updateLessonFormField('meetUrl', e.target.value)}
                      placeholder="https://zoom.us/j/123456789"
                      className="form-input"
                    />
                  </div>
                </div>
              )}

              {lessonFormData.iltsType === 'Offline' && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Venue</label>
                    <input
                      type="text"
                      value={lessonFormData.eventVenue || ''}
                      onChange={(e) => updateLessonFormField('eventVenue', e.target.value)}
                      placeholder="Enter venue address"
                      className="form-input"
                    />
                  </div>
                </div>
              )}
            </>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={lessonFormData.description || ''}
                onChange={(e) => updateLessonFormField('description', e.target.value)}
                placeholder="Enter lesson description"
                className="form-textarea"
                rows={3}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
            <button
              onClick={handleSaveLesson}
              disabled={saving}
              className="btn-primary"
            >
              <Save size={18} />
              {saving ? 'Saving...' : 'Save Lesson'}
            </button>
            <button onClick={handleResetForm} className="btn-secondary">
              Reset Form
            </button>
          </div>
        </div>
      </div>

      <div className="s-admin-upload-sidebar">
        <div className="s-admin-upload-guidelines-box">
          <h4>Lesson Creation Guidelines</h4>
          <ul className="s-admin-upload-guidelines-list">
            <li>✓ Lessons are not linked to any course</li>
            <li>✓ Support for Content-Based and ILTS lessons</li>
            <li>✓ Content types: Document, Video, SCORM, URL</li>
            <li>✓ Files can be uploaded during creation</li>
            <li>✓ Lessons can be associated with courses later</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default UploadTab;
